import { useEffect, useState } from "react";
import { formatApiErrorDetail } from "../../api";
import { OWNERS, OWNER_ORDER, STATUSES, STATUS_LABEL } from "../workplan/util";
import { PRIORITY_LABEL, PRIORITY_ORDER } from "./labels";

// The same drawer the Queue uses, so the gesture to close it is one Ward has
// already learnt: tap the scrim, or Escape.
export default function TaskEditor({ node, onSave, onDelete, onClose }) {
  const [draft, setDraft] = useState(node);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => { setDraft(node); setError(""); setConfirmingDelete(false); }, [node]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });
  const isTask = node.level === "task";

  const save = async () => {
    setBusy(true);
    setError("");
    // Only what actually changed. A patch that resent every field would
    // rewrite `status` from a stale draft if Ward ticked the box behind the
    // open drawer.
    const patch = {};
    const fields = isTask
      ? ["title", "owner", "priority", "due", "est", "doneWhen", "status"]
      : ["title"];
    for (const f of fields) {
      if ((draft[f] ?? "") !== (node[f] ?? "")) patch[f] = draft[f] ?? "";
    }
    try {
      if (Object.keys(patch).length) await onSave(node.id, patch);
      onClose();
    } catch (e) {
      // The drawer stays open with the draft intact — the alternative is
      // retyping a done-when paragraph.
      setError(formatApiErrorDetail(e?.response?.data?.detail));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await onDelete(node.id);
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="mk-drawer-scrim" onClick={onClose} data-testid="mk-task-editor-scrim">
      <aside className="mk-drawer" onClick={(e) => e.stopPropagation()}
        data-testid="mk-task-editor">
        <div className="mk-drawer-head">
          <span className="mk-pill">
            Editing {isTask ? "a task" : `a ${node.level}`}
          </span>
          <button type="button" className="mk-btn-quiet" onClick={onClose}
            data-testid="mk-task-editor-close">Close</button>
        </div>

        <label className="mk-label" htmlFor="mk-task-title">Title</label>
        <input id="mk-task-title" className="mk-input" value={draft.title || ""}
          onChange={set("title")} data-testid="mk-task-title" />

        {/* A theme and an epic are headings. Their progress is the rollup of
            the tasks underneath, so an owner or a due date on one would be a
            second answer to a question the tasks already answer. */}
        {!isTask && (
          <div className="mk-hint">
            Themes and epics only carry a name — the work, and who owes it,
            lives on the tasks underneath.
          </div>
        )}

        {isTask && (
          <>
            <label className="mk-label" htmlFor="mk-task-owner">Owner</label>
            <select id="mk-task-owner" className="mk-select" value={draft.owner || ""}
              onChange={set("owner")} data-testid="mk-task-owner">
              <option value="">Nobody yet</option>
              {OWNER_ORDER.map((o) => <option key={o} value={o}>{OWNERS[o]}</option>)}
            </select>

            <label className="mk-label" htmlFor="mk-task-priority">Priority</label>
            <select id="mk-task-priority" className="mk-select"
              value={draft.priority || "normal"} onChange={set("priority")}
              data-testid="mk-task-priority">
              {PRIORITY_ORDER.map((p) => (
                <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>
              ))}
            </select>

            <label className="mk-label" htmlFor="mk-task-due">Due</label>
            <input id="mk-task-due" className="mk-input" type="date"
              value={draft.due || ""} onChange={set("due")} data-testid="mk-task-due" />

            <label className="mk-label" htmlFor="mk-task-est">How long it takes</label>
            <input id="mk-task-est" className="mk-input" value={draft.est || ""}
              onChange={set("est")} placeholder="30 min" data-testid="mk-task-est" />

            <label className="mk-label" htmlFor="mk-task-donewhen">Done when</label>
            <textarea id="mk-task-donewhen" className="mk-textarea" rows={4}
              value={draft.doneWhen || ""} onChange={set("doneWhen")}
              placeholder="What has to be true for this to be ticked?"
              data-testid="mk-task-donewhen" />

            <label className="mk-label" htmlFor="mk-task-status">Status</label>
            <select id="mk-task-status" className="mk-select" value={draft.status || "todo"}
              onChange={set("status")} data-testid="mk-task-status">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </>
        )}

        {error && (
          <div className="mk-error" data-testid="mk-task-editor-error">
            <div>Not saved. {error}</div>
            <div>Your edits are still here — try Save again.</div>
          </div>
        )}

        <div className="mk-row-end">
          {!confirmingDelete && (
            <button type="button" className="mk-btn-quiet danger" disabled={busy}
              onClick={() => setConfirmingDelete(true)} data-testid="mk-task-delete">
              Delete
            </button>
          )}
          {confirmingDelete && (
            <button type="button" className="mk-btn-quiet danger" disabled={busy}
              onClick={remove} data-testid="mk-task-delete-confirm">
              {/* Says what goes, because deleting a theme takes its epics and
                  every task under it. */}
              {isTask ? "Delete this task" : `Delete this ${node.level} and everything under it`}
            </button>
          )}
          <button type="button" className="mk-btn-quiet" disabled={busy}
            onClick={onClose}>Cancel</button>
          <button type="button" className="mk-btn" disabled={busy} onClick={save}
            data-testid="mk-task-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </aside>
    </div>
  );
}
